import { useState } from "react";
import ApiService from "../../services/api.service";

// Shared create/edit player modal — used by CoachPlayersPage (roster "+ Add
// Player"), CoachPlayerStatsPage ("Edit player"), and (since 2026-08-22)
// LeagueTeamsPage's roster block. Extracted out of CoachPlayersPage so every
// page can open the same form without duplicating the create/update/archive
// calls.
//
// `teamId` is only actually sent on CREATE for the COACH routes — it's
// required once the coach has 2+ teams (the server 400s teamIdRequired
// without it) so the new player lands on the right roster. Coach
// update/archive don't need it: the server resolves the owning team itself
// across all of the coach's teams.
//
// The optional `api` prop overrides those three calls, which is how the
// league tier reuses this form. The league routes carry teamId on all three
// verbs (the Lambda scopes every write by (leagueId, teamId)), so they can't
// share the coach shape — but everything else about the form is identical, so
// injecting the calls beats a second copy. Omit `api` and the coach behavior
// is unchanged.

export const POSITIONS = ["", "PG", "SG", "SF", "PF", "C"];

// CSS variable name per position — see clientTheme.css's --position-* tokens
// (light/dark pair mirroring iOS PolypDesign.positionColor). The empty/clear
// option has no color of its own; the chip falls back to --text-secondary.
const POSITION_COLOR_VAR = {
  PG: "--position-pg",
  SG: "--position-sg",
  SF: "--position-sf",
  PF: "--position-pf",
  C: "--position-c",
};

export default function PlayerModal({ player, teamId, api, onClose, onSaved, onArchived }) {
  const [firstName, setFirstName] = useState(player?.firstName || "");
  const [lastName, setLastName] = useState(player?.lastName || "");
  const [number, setNumber] = useState(player?.number || "");
  const [position, setPosition] = useState(player?.position || "");
  const [dob, setDob] = useState(player?.dob || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEditing = !!player;
  const canSave = firstName.trim() || lastName.trim();

  const calls = api || {
    create: (payload) => ApiService.createCoachPlayer(teamId, payload),
    update: (playerId, payload) => ApiService.updateCoachPlayer(playerId, payload),
    archive: (playerId) => ApiService.archiveCoachPlayer(playerId),
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = { firstName, lastName, number, position, dob };
    try {
      if (isEditing) {
        await calls.update(player.playerId, payload);
      } else {
        await calls.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("Remove this player? Past game stats are kept.")) return;
    setSaving(true);
    try {
      await calls.archive(player.playerId);
      onArchived();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">{isEditing ? "Edit Player" : "Add Player"}</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="First name" value={firstName}
            onChange={(e) => setFirstName(e.target.value)} />
          <input className="client-input" placeholder="Last name" value={lastName}
            onChange={(e) => setLastName(e.target.value)} />
          <input className="client-input" placeholder="Jersey number" value={number}
            onChange={(e) => setNumber(e.target.value.slice(0, 3))} />
          <div className="coach-position-chips">
            {POSITIONS.map((p) => (
              <button
                key={p || "none"}
                type="button"
                className={`coach-position-chip${position === p ? " selected" : ""}`}
                style={POSITION_COLOR_VAR[p] ? { "--chip-color": `var(${POSITION_COLOR_VAR[p]})` } : undefined}
                onClick={() => setPosition(p)}
              >
                {p || "—"}
              </button>
            ))}
          </div>
          <input type="date" className="client-input" value={dob}
            onChange={(e) => setDob(e.target.value)} />
        </div>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          {isEditing && (
            <button className="client-button-secondary" onClick={archive} disabled={saving}>
              Remove
            </button>
          )}
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Saving…" : isEditing ? "Save" : "Add Player"}
          </button>
        </div>
      </div>
    </div>
  );
}
